// ==================== 类型定义 ====================
/**
 * 评估事项（最细粒度）
 */
export interface AssessmentItemRecord {
  key: string;
  assessmentItem: string; // 评估事项
  description: string; // 说明
}

/**
 * 评估维度（二级）
 */
export interface EvaluationDimensionRecord {
  key: string;
  evaluationDimension: string; // 评估维度
  children: AssessmentItemRecord[]; // 下属评估事项
}

/**
 * 评估阶段（一级）
 */
export interface AssessmentPhaseRecord {
  key: string;
  assessmentPhase: string; // 评估阶段
  children: EvaluationDimensionRecord[]; // 下属评估维度
}

/**
 * 展平后的表格记录（用于表格显示）
 */
export interface ExcellentReviewTableRecord {
  key: string;
  assessmentPhase?: string; // 评估阶段（一级）
  evaluationDimension?: string; // 评估维度（二级）
  assessmentItem?: string; // 评估事项（三级）
  description?: string; // 说明
  level: number; // 层级：1=阶段，2=维度，3=事项
  children?: ExcellentReviewTableRecord[]; // 用于树形展示
}

// ==================== 常量配置 ====================
/**
 * 优评表格树形数据
 * 结构：阶段 → 维度 → 事项
 */
export const EXCELLENT_REVIEW_TABLE_DATA: AssessmentPhaseRecord[] = [
  {
    key: "phase-1",
    assessmentPhase: "前置筛选评估",
    children: [
      {
        key: "dimension-1-1",
        evaluationDimension: "供应商背景",
        children: [
          {
            key: "item-1-1-1",
            assessmentItem: "供应商资质",
            description: "这里是说明文字",
          },
        ],
      },
      {
        key: "dimension-1-2",
        evaluationDimension: "技术维度",
        children: [
          {
            key: "item-1-2-1",
            assessmentItem: "资质文件符合性",
            description: "这里是说明文字",
          },
        ],
      },
      {
        key: "dimension-1-3",
        evaluationDimension: "质量维度",
        children: [
          {
            key: "item-1-3-1",
            assessmentItem: "质量体系",
            description: "这里是说明文字",
          },
          {
            key: "item-1-3-2",
            assessmentItem: "工艺研究符合性",
            description: "这里是说明文字",
          },
        ],
      },
    ],
  },
  {
    key: "phase-2",
    assessmentPhase: "基本准入评估",
    children: [
      {
        key: "dimension-2-1",
        evaluationDimension: "供应商背景",
        children: [
          {
            key: "item-2-1-1",
            assessmentItem: "供应商资质",
            description: "这里是说明文字",
          },
          {
            key: "item-2-1-2",
            assessmentItem: "资质文件符合性",
            description: "这里是说明文字",
          },
        ],
      },
      {
        key: "dimension-2-2",
        evaluationDimension: "技术维度",
        children: [
          {
            key: "item-2-2-1",
            assessmentItem: "资质文件符合性",
            description: "这里是说明文字",
          },
        ],
      },
      {
        key: "dimension-2-3",
        evaluationDimension: "质量维度",
        children: [
          {
            key: "item-2-3-1",
            assessmentItem: "质量体系",
            description: "这里是说明文字",
          },
        ],
      },
    ],
  },
  {
    key: "phase-3",
    assessmentPhase: "正式准入评估结果",
    children: [
      {
        key: "dimension-3-1",
        evaluationDimension: "供应商背景",
        children: [
          {
            key: "item-3-1-1",
            assessmentItem: "供应商资质",
            description: "这里是说明文字",
          },
        ],
      },
    ],
  },
];

// ==================== 数据转换函数 ====================
/**
 * 将树形数据展平为一维数组
 * @param data - 树形数据
 * @returns 展平后的数据数组
 */
export const flattenTreeData = (
  data: AssessmentPhaseRecord[],
): ExcellentReviewTableRecord[] => {
  const result: ExcellentReviewTableRecord[] = [];

  data.forEach((phase) => {
    phase.children.forEach((dimension) => {
      dimension.children.forEach((item) => {
        result.push({
          key: item.key,
          assessmentPhase: phase.assessmentPhase,
          evaluationDimension: dimension.evaluationDimension,
          assessmentItem: item.assessmentItem,
          description: item.description,
          level: 3, // 最细粒度都是三级
        });
      });
    });
  });

  return result;
};

// ==================== 动态计算合并行数的函数 ====================
/**
 * 动态计算需要合并的行索引
 * 根据实际数据，计算出每个相同值的起始行应该合并多少行
 * 返回格式: { rowIndex: spanCount }
 *
 * @param data - 表格数据数组
 * @param columnKey - 需要计算合并的列名
 */
export const getRowSpanMap = (
  data: ExcellentReviewTableRecord[],
  columnKey: keyof ExcellentReviewTableRecord,
): Record<number, number> => {
  const spanMap: Record<number, number> = {};
  let currentValue = "";
  let startIndex = 0;

  data.forEach((record, index) => {
    const value = record[columnKey] as string;

    // 如果当前值与之前的值不同，记录前一个值的合并行数
    if (value !== currentValue) {
      if (currentValue !== "") {
        spanMap[startIndex] = index - startIndex;
      }
      currentValue = value;
      startIndex = index;
    }
  });

  // 处理最后一个值
  if (currentValue !== "") {
    spanMap[startIndex] = data.length - startIndex;
  }

  return spanMap;
};

/**
 * 动态计算评估阶段的合并行数
 *
 * @param data - 表格数据数组
 */
export const getPhaseRowSpanMap = (
  data: ExcellentReviewTableRecord[],
): Record<number, number> => {
  const spanMap: Record<number, number> = {};
  let currentPhase = "";
  let startIndex = 0;

  data.forEach((record, index) => {
    const phase = record.assessmentPhase || "";

    // 当阶段变化时，记录前一个阶段的合并行数
    if (phase !== currentPhase) {
      if (currentPhase !== "") {
        spanMap[startIndex] = index - startIndex;
      }
      currentPhase = phase;
      startIndex = index;
    }
  });

  // 处理最后一个阶段
  if (currentPhase !== "") {
    spanMap[startIndex] = data.length - startIndex;
  }

  return spanMap;
};
